import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import { TOOL_NAMES } from 'js/src/agent/toolNames';
import type { Skill } from '../types';

export const createStoryboardVideoSkill: Skill = {
    name: SKILL_NAMES.createStoryboardVideo,
    description:
        'Build a video from a scene-by-scene plan the user already wrote — numbered scenes, per-beat timecodes, or paired voiceover/visual directions. Use whenever the request already specifies what each scene contains, rather than naming a topic to write about. Load it even when the plan also asks for things Lumen5 cannot do (sound effects, transitions, a named commercial track, an exact runtime) — it covers how to substitute for those and build anyway.',
    kind: 'video',
    surfaces: ['sketch'],
    instructions: `# Create Storyboard Video
The user has already decided what the video contains, beat by beat. Every
other recipe writes the content; this one **transcribes a plan that already
exists**. The brief is the specification — your job is fidelity to it, then
making it as good as the toolbox allows.

Briefs typically arrive in four shapes. Recognize which you have, because it
tells you what the user has pinned down and what they've left to you:
- **Scene list** — numbered scenes, each with copy and often a media
  keyword. No timings, so pacing is yours.
- **Timed beats** — the same, plus per-scene durations or ranges. Pacing is
  theirs; hold to it.
- **Voiceover + visual pairs** — each beat labels spoken narration
  separately from what's on screen. The two are not interchangeable.
- **Timecode script** — no scene labels at all; bracketed timecodes carry
  the structure. Infer scene boundaries from the timings.

**Most of these briefs are LLM output the user lightly reviewed.** They want
the video their plan describes, not a literal-minded transcription that
inherits every impossible instruction. Make your best attempt at everything,
substitute sensibly where you must, and say plainly at the end what you
changed. Refusing the brief, or dropping a beat silently, are both defects.

**Always build the video in this turn.** A brief is a complete instruction to
proceed, so never end a turn having only described what you would do, listed
what isn't possible, or asked for an asset. Missing pieces are things you
substitute and report, not reasons to stop: a logo you don't have becomes the
brand name as text, an unavailable track becomes the closest match. If the
questionnaire is dismissed or unanswered, proceed on your best judgment and
build. The user should always end the turn with a finished video plus a note
on what changed — never a question and an empty canvas.

**Every question goes in the questionnaire, including the awkward ones.** A
brief that opens with something we can't do — a commercial track, an asset
you weren't given, an effect with no tool — is still a brief to build, not a
reason to reply in prose and wait. Raise it as a choice inside the one
\`${TOOL_NAMES.askUserQuestion}\` call from Setup, alongside the ordinary questions, then build with
your stated default whatever comes back. Prose that ends the turn with a
question is the failure this rule exists to prevent.

## Setup
Use \`${SKILL_NAMES.gatherRequirements}\` to get necessary info, skipping anything already
provided. A storyboard answers most of the standard questionnaire on its own —
do not re-ask for a script, scene content, or a duration the brief already
states. Beyond that skill's suggestions, resolve:
- **Media strategy — always ask this one.** A brief describes specific shots:
  a particular person doing a particular thing, in particular light. Stock
  search will get you the subject, not that exact frame, so the user has to
  choose the tradeoff. Offer, as a \`radio\`: stock media
  (closest real footage), AI-generated media (matches the description more
  literally, costs generation), or their own uploads (\`file_upload\` if they
  pick it). Ask once for the video, not per scene.
- **Anything the brief asks for that this recipe cannot do** (see below), and
  **any asset the brief references but didn't attach** — a logo, a product
  shot, a headshot. Both belong in this questionnaire as a choice paired with
  the substitution you intend ("use a text wordmark" / "upload the logo"),
  never as a message asking the user to come back with it. They are offered
  in passing, never a gate: an unanswered one resolves to your stated
  default, not to stopping.
- **Narration, only when the brief is ambiguous about it.** A brief with
  labeled \`Voiceover:\` or \`NARRATOR:\` lines wants a voiceover; one with only
  on-screen copy wants text on media. Ask when neither is clear, not when
  the labels already answer it. When those labels have settled it, the voice
  question should set \`require_voiceover\` so it stops offering "No voiceover"
  — an answer that would contradict the brief the user just gave you.

## Structure — one scene per shot
- **Every beat in the brief becomes at least one scene, in the brief's
  order.** Never merge two beats to save effort, and never invent a beat the
  brief doesn't contain.
- **A beat describing multiple shots becomes one scene per shot.** A beat
  that moves from one subject to another, or says "then", "cut to" or "next
  we see", is describing two pictures and becomes two scenes. A beat holding
  two things in one frame — a split screen, a before-and-after side by side,
  an inset — is one. The test is whether the picture fully replaces the
  previous one: a cut is a new scene, a composition is not. Split the beat's
  copy and its time across the scenes you create, and keep them adjacent so
  the beat still reads as one unit.
- **Honor stated timings per scene, not just in total.** A brief giving each
  beat a range is describing rhythm; hitting the total while flattening
  every scene to an equal share loses exactly what they specified. Set them
  with \`${TOOL_NAMES.setSceneDuration}\`. When one beat splits into several shots, divide
  that beat's stated time between them.
- **A stated total duration is a target, not a guarantee** — land close and
  say what you got. Where a stated timing is too short to read the text it
  carries, lengthen it and flag that, rather than shipping a scene nobody
  can read.
- **Coming in far under the brief is as wrong as running over.** On a
  voicedriven build the runtime follows the narration, so condensing every
  spoken line yields a video half the length that was asked for — every beat
  present, and the pacing nothing like what was specified. Budget each beat's
  narration to its stated time using the word-count maths in
  \`${SKILL_NAMES.writeVoicedrivenVideoScript}\`, and keep the brief's own sentences rather
  than trimming them to headlines. Landing well short of a stated total means
  the narration was over-trimmed — restore it, rather than padding scene
  durations past what's spoken.
- Untimed briefs: pace from the text, per \`${TOOL_NAMES.lintVideo}\`'s \`scene_reading_time\`.

## Scene design
- **Choose each design to fit the beat's stated content** — the number of
  text assets it actually needs, and whether it carries media. A design
  missing a needed asset is the wrong design.
- **On-screen text is what the brief marks as on-screen text.** Copy labeled
  \`Text:\`, \`On-screen text:\` or given as a bare line is display copy: set it
  with \`${TOOL_NAMES.setText}\` close to verbatim. Fix typos, drop a trailing
  period, split an overlong line across two assets — do not rewrite,
  summarize, or "improve" wording the user chose.
- **Narration is never silently promoted to on-screen text.** Lines labeled
  \`Voiceover:\`, \`VO:\`, \`NARRATOR:\` or \`Narration:\` are spoken. Putting them on
  screen as body copy on a silent video is the single most common failure in
  this format, and it is a defect. If the user declined a voiceover, say the
  narration can't be spoken instead of pasting it into the picture.
- **Direction is not copy.** \`[FADE IN]\`, \`SFX:\`, \`Camera:\`, \`Visual:\`,
  \`--ar 9:16\`, \`Media keyword:\` and style tags ("photorealistic, 8k") are
  instructions to you. None of them are ever displayed. Media keywords and
  visual descriptions drive the media search; the rest inform your choices
  or get flagged as unsupported.
- Vary designs across the video and avoid the same design twice in a row,
  unless the brief explicitly calls for a repeated treatment.

## Asset timing — follow the brief's beat boundaries
- For a voiceover build, anchor each scene to the words of its own beat's
  narration (\`${TOOL_NAMES.addVoiceoverScene}\` with \`words\`) so a scene turns over exactly
  when the brief says the next beat starts.
- Where a beat split into several shots, each shot takes the span of the
  narration that describes it — not an even division of the beat's words.

## Media
- Load \`${SKILL_NAMES.findUserMedia}\` before searching the user's library,
  \`${SKILL_NAMES.findStockMedia}\` before your first stock search, and \`${SKILL_NAMES.findDamMedia}\`
  when a DAM is connected. Follow the strategy chosen in Setup.
- **Search the subject, not the prose.** A visual description is written for
  a human or an image model; strip it to the searchable subject and drop the
  style tags, camera direction, lighting notes and aspect flags. A long
  cinematic sentence about one animal, rendering style and lens included, is
  a search for that animal's name.
- **Close and real beats exact and generic.** When stock can't show the
  described shot, prefer the nearest genuine footage of the subject over a
  vaguely-related clip — an unrelated stock image is worse than a simpler
  one that actually shows the thing.
- **AI media, when that's the chosen strategy:** \`${TOOL_NAMES.generateAiMedia}\` makes one
  image, or one 5-second video clip. Generated video is gated on the user's
  plan — if it isn't available, use \`${TOOL_NAMES.suggestUpgrade}\` for
  \`generate_ai_video_clip\` rather than silently falling back. A beat wanting
  more than 5 seconds of moving footage cannot be one generated clip: use a
  generated still, or several clips across the shots you split that beat
  into, and say which you did.
- Reframe so the subject is composed and nothing important sits under text
  (\`${SKILL_NAMES.adjustMedia}\` skill).

## Requests this recipe cannot fulfil
Attempt everything else, substitute where there's a reasonable equivalent,
and collect what's left for the closing summary. Never refuse a whole brief
over these.
- **Sound effects.** There is no SFX tool. Music is available via
  \`${TOOL_NAMES.searchMusic}\`/\`${TOOL_NAMES.setMusic}\`, so serve the intent with a track whose
  energy matches. A cue tied to one moment in one beat can't be placed.
- **Transitions, camera moves and speed effects.** No tool sets a named
  transition between scenes, a move across or into a shot, or a change of
  playback speed. Scene designs carry their own motion; choose ones whose
  energy suits the brief and drop the named effect.
- **A specific commercial track.** Search \`${TOOL_NAMES.searchMusic}\` for something of
  similar mood and name the substitution.
- **An exact runtime.** Durations are targets. Say what you landed on.
- **Named or recurring characters, and continuous action.** Stock and
  generated media cannot hold one invented person across scenes, and a beat
  needing a specific performance won't be found. Flag this early — a brief
  built on a character arc, dialogue between named people, or a plot that
  needs continuity of action is not a video this toolbox can make. Offer what
  it can: the same story told with representative footage, or a text-led
  treatment carrying the narrative. Don't quietly cast a different stranger
  in every scene and present it as the brief.
- **Photoreal humans on demand, real people's likenesses, and copyrighted
  characters or footage** stay refused outright.

## Styling
- Colors and styling come from the template or the user's brand kit — apply a
  kit when one exists or the user references their brand
  (\`${SKILL_NAMES.applyBrandKit}\` skill). A brief naming brand colors is a reason to
  apply the kit, not to hand-pick overrides.
- Honor a stated aspect ratio, including one implied by a \`9:16\`/\`16:9\` flag in
  the visual prompts, by passing it to \`${TOOL_NAMES.createBlankVideo}\` when you create
  the video rather than applying it afterwards.

## Finishing
Load \`${SKILL_NAMES.reviewVideo}\` for the standard pass. On top of that, run a
**scene-by-scene check against the brief** — the review that matters most in
this format, and one the standard pass cannot do because only you have the
brief. Read the built video with \`${TOOL_NAMES.getVideoState}\` and walk the brief top to
bottom, pairing each beat with the scene or scenes it produced. For every
pair, confirm:
- **The beat is there, in the brief's position, and nothing was invented.**
  A missing beat, a reordered one, or an extra scene with no beat behind it
  is a defect — fix it and re-check rather than noting it.
- **Its multi-shot beats became one scene per shot**, still adjacent and in
  the brief's order.
- **The on-screen text is that beat's display copy, close to verbatim** — not
  a paraphrase, not a summary, and not a narration line promoted to body
  copy.
- **No direction leaked into the picture** — no \`SFX:\`, \`Visual:\`, \`Camera:\`,
  \`[FADE IN]\`, \`Media keyword:\`, style tags or aspect flags visible anywhere.
- **Its media shows what the beat describes**, or is the closest real thing
  you could source — never a clip unrelated to the beat.
- **Its stated timing is reflected in the scene's actual duration**, and any
  scene you lengthened for readability is one you'll mention.

Then, before handing back:
- **If the video has a voiceover, turn captions off** (\`${TOOL_NAMES.setCaptions}\` with
  \`enabled: false\`) unless the brief or the user asked for them on.
- **The final message must account for everything the brief asked for that
  the video does not do.** This is required, not optional, and it is the last
  thing you do. List every unsupported request (sound effects, transitions and
  camera moves, a named commercial track, an exact runtime, character
  continuity), what you did instead for each, and the duration you landed on
  against the one they asked for. Name the specific request, not a vague
  caveat: "no per-scene SFX — the toolbox has no sound-effect tool, so the
  music bed carries the energy instead" tells them something; "some effects
  weren't possible" does not. If the brief asked for nothing outside what was
  built, say that too, so silence never has to be interpreted. A build that
  quietly dropped four instructions looks broken; the same build with an
  honest list reads as finished.`,
};
